import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseIds } from '../event/useEventSelection';

export const EVENTS_PARAM = 'events';
export const MAX_SELECTED_EVENTS = 4;

export interface AssetSelection {
    selectedKeys: string[];
    toggleEvent: (key: string) => void;
    clearEvents: () => void;
}

export function toggleKey(
    keys: readonly string[],
    key: string,
    max: number = MAX_SELECTED_EVENTS
): string[] {
    if (keys.includes(key)) return keys.filter(k => k !== key);
    if (keys.length >= max) return [...keys];
    return [...keys, key];
}

export function useAssetSelection(): AssetSelection {
    const [searchParams, setSearchParams] = useSearchParams();
    const param = searchParams.get(EVENTS_PARAM);
    const selectedKeys = useMemo(
        () => parseIds(param).slice(0, MAX_SELECTED_EVENTS),
        [param]
    );

    const write = useCallback(
        (keys: string[]) => {
            setSearchParams(
                prev => {
                    const params = new URLSearchParams(prev);
                    if (keys.length > 0)
                        params.set(EVENTS_PARAM, keys.join(','));
                    else params.delete(EVENTS_PARAM);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const toggleEvent = useCallback(
        (key: string) => write(toggleKey(selectedKeys, key)),
        [write, selectedKeys]
    );
    const clearEvents = useCallback(() => write([]), [write]);

    return { selectedKeys, toggleEvent, clearEvents };
}
